// Contratos institucionales, efectivos (pax) diarios y MRP de compras.
// Catering → Operación → Contratos y pax.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Handshake, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { fieldCls, inv, num } from '@/features/inventario/shared';
import {
  CompraSugerida, Contrato, ContratoPrecio, Efectivo, SERVICIO_LABEL,
  cat, catRpc, invalidateCatering, money,
} from './shared';

interface PartnerOpt { id: string; razon_social: string }
interface BodegaOpt { id: string; codigo: string; nombre: string }

const isoHoy = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const isoMas = (dias: number): string => {
  const d = new Date(); d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function ContratosCatering() {
  const qc = useQueryClient();
  const [selId, setSelId] = useState('');
  const [fecha, setFecha] = useState(isoHoy());
  const [servido, setServido] = useState<Record<string, string>>({});
  const [desde, setDesde] = useState(isoHoy());
  const [hasta, setHasta] = useState(isoMas(6));
  const invalidar = () => invalidateCatering(qc);

  const { data: contratos = [] } = useQuery<Contrato[]>({
    queryKey: ['cat-contratos'],
    queryFn: () => cat.list<Contrato>('contratos', '?select=*&deleted_at=is.null&order=codigo'),
  });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({
    queryKey: ['cat-partners'],
    queryFn: async () =>
      (await apiClient.get<PartnerOpt[]>('/rest/financiero.partner?select=id,razon_social&order=razon_social&limit=300')).data,
  });
  const { data: precios = [] } = useQuery<ContratoPrecio[]>({
    queryKey: ['cat-contrato-precios'],
    queryFn: () => cat.list<ContratoPrecio>('contrato_precios', '?select=*&deleted_at=is.null'),
  });
  const { data: efectivos = [] } = useQuery<Efectivo[]>({
    queryKey: ['cat-efectivos', fecha],
    queryFn: () => cat.list<Efectivo>('efectivos', `?select=*&fecha=eq.${fecha}&deleted_at=is.null`),
  });
  const { data: bodegas = [] } = useQuery<BodegaOpt[]>({
    queryKey: ['cat-bodegas'],
    queryFn: () => inv.list<BodegaOpt>('locations', '?select=id,codigo,nombre&deleted_at=is.null&es_activo=eq.true&order=nombre'),
  });
  const bodegaMp = bodegas.find((b) => b.codigo === 'CC01')?.id || bodegas[0]?.id || '';

  const { data: sugerido = [], refetch: recalcular, isFetching } = useQuery<CompraSugerida[]>({
    queryKey: ['cat-compras-sugerido', desde, hasta, bodegaMp], enabled: false,
    queryFn: () => catRpc<CompraSugerida[]>('compras_sugerido', { _desde: desde, _hasta: hasta, _bodega_mp_id: bodegaMp }),
  });

  const guardarServida = useMutation({
    mutationFn: ({ id, cantidad }: { id: string; cantidad: number }) =>
      cat.update('efectivos', id, { cantidad_servida: cantidad }),
    onSuccess: () => { toast.success('Efectivos confirmados'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const partnerName = (id: string) => partners.find((p) => p.id === id)?.razon_social ?? '—';
  const preciosDe = (cid: string) => precios.filter((p) => p.contrato_id === cid);
  const efectivosDe = (cid: string) => efectivos.filter((e) => e.contrato_id === cid);
  const totalComprar = useMemo(() => sugerido.reduce((s, i) => s + Number(i.costo_compra || 0), 0), [sugerido]);

  return (
    <div className="space-y-4 p-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Contratos */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Handshake className="h-4 w-4" /> Contratos institucionales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {contratos.map((c) => (
              <button key={c.id} onClick={() => setSelId(c.id)}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm hover:bg-accent ${selId === c.id ? 'border-primary bg-accent' : ''}`}>
                <span className="flex items-center justify-between">
                  <span className="font-medium">{c.codigo} — {partnerName(c.partner_id)}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs">corte {c.corte}</span>
                </span>
                <span className="block text-xs text-muted-foreground">{c.nombre}</span>
                <span className="block pt-1 text-xs">
                  {preciosDe(c.id).map((p) => (
                    <span key={p.id} className="mr-3 font-mono">
                      {SERVICIO_LABEL[p.servicio] ?? p.servicio}: {money(p.precio, 2)} × {num(p.pax_pactado, 0)} pax
                    </span>
                  ))}
                </span>
              </button>
            ))}
            {!contratos.length && <p className="text-sm text-muted-foreground">Sin contratos registrados.</p>}
          </CardContent>
        </Card>

        {/* Efectivos del día */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Efectivos (pax) del día</CardTitle>
            <div className="flex items-end gap-2 pt-1">
              <input type="date" className={`${fieldCls} w-44`} value={fecha} onChange={(e) => setFecha(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                Plan = lo pedido por el cliente · Servido = lo confirmado en sitio (alimenta facturación y rendimiento)
              </p>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Contrato</th><th className="py-1 pr-2">Servicio</th>
                <th className="py-1 pr-2 text-right">Plan</th><th className="py-1 pr-2 text-right">Servido</th><th />
              </tr></thead>
              <tbody>
                {contratos.flatMap((c) => efectivosDe(c.id).map((e) => (
                  <tr key={e.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 text-xs">{c.codigo} · {partnerName(c.partner_id)}</td>
                    <td className="py-1.5 pr-2">{SERVICIO_LABEL[e.servicio] ?? e.servicio}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{num(e.cantidad_plan, 0)}</td>
                    <td className="py-1.5 pr-2 text-right">
                      <input className={`${fieldCls} w-24 text-right`}
                        placeholder={e.cantidad_servida != null ? String(e.cantidad_servida) : String(e.cantidad_plan)}
                        value={servido[e.id] ?? ''} onChange={(ev) => setServido((s) => ({ ...s, [e.id]: ev.target.value }))} />
                    </td>
                    <td className="py-1.5 text-right">
                      <Button size="sm" variant="outline" disabled={!servido[e.id] || guardarServida.isPending}
                        onClick={() => { guardarServida.mutate({ id: e.id, cantidad: parseFloat(servido[e.id]) }); setServido((s) => ({ ...s, [e.id]: '' })); }}>
                        Confirmar
                      </Button>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
            {!efectivos.length && <p className="pt-2 text-sm text-muted-foreground">Sin efectivos registrados para esta fecha.</p>}
          </CardContent>
        </Card>
      </div>

      {/* MRP compras */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><ShoppingCart className="h-4 w-4" /> Sugerido de compras (MRP)</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div><p className="pb-1 text-xs text-muted-foreground">Desde</p>
              <input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
            <div><p className="pb-1 text-xs text-muted-foreground">Hasta</p>
              <input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
            <Button disabled={isFetching} onClick={() => void recalcular()}>
              {isFetching ? 'Calculando…' : 'Explotar menú → lista de compras'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Menú planificado × pax − stock en bodega = lo que hay que comprar, en su unidad de compra real.
            </p>
          </div>
        </CardHeader>
        {sugerido.length > 0 && (
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Materia prima</th>
                <th className="py-1 pr-2">Presentación de compra</th>
                <th className="py-1 pr-2 text-right">Necesidad</th><th className="py-1 pr-2 text-right">Stock</th>
                <th className="py-1 pr-2 text-right">Comprar</th><th className="py-1 pr-2 text-right">Costo compra</th>
              </tr></thead>
              <tbody>
                {sugerido.map((s, i) => (
                  <tr key={s.material_id} className={`border-b last:border-0 ${Number(s.comprar) > 0 ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                    <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-1 pr-2">{s.nombre}<span className="ml-1 text-xs text-muted-foreground">{s.unidad}</span></td>
                    <td className="py-1 pr-2 text-muted-foreground">{s.presentacion ?? '—'}</td>
                    <td className="py-1 pr-2 text-right font-mono">{num(s.necesidad)}</td>
                    <td className="py-1 pr-2 text-right font-mono">{num(s.stock)}</td>
                    <td className="py-1 pr-2 text-right font-mono font-medium">{Number(s.comprar) > 0 ? num(s.comprar) : '—'}</td>
                    <td className="py-1 pr-2 text-right font-mono">{Number(s.costo_compra) > 0 ? money(s.costo_compra, 2) : '—'}</td>
                  </tr>
                ))}
                <tr className="font-medium">
                  <td colSpan={6} className="py-1.5 pr-2 text-right">Total a comprar</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{money(totalComprar, 2)}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
